/**
 * WorksheetBuilder - Main builder canvas for creating worksheets
 *
 * Features:
 * - Left sidebar: Component palette
 * - Center: Worksheet canvas (drag-drop area)
 * - Right sidebar: Property editor for selected component
 * - Top bar: Title, save, preview buttons
 * - Drag-and-drop using @dnd-kit
 * - Auto-save with debounce
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Save,
  Eye,
  Undo2,
  Redo2,
  Grid3X3,
  Loader2,
  ArrowLeft,
  FileText,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { ComponentPalette } from "./ComponentPalette";
import { WorksheetCanvas } from "./WorksheetCanvas";
import { PropertyEditor } from "./PropertyEditor";
import { WorksheetPreview } from "./WorksheetPreview";
import {
  createComponent,
  createEmptyWorksheet,
  generateComponentId,
  type WorksheetTemplate,
  type WorksheetComponent,
  type ComponentDefinition,
} from "./types";
import type { CurriculumOutcome } from "@/components/planner/types";

// ============================================================================
// Types
// ============================================================================

export interface WorksheetBuilderProps {
  /** Initial worksheet data (for editing existing) */
  initialWorksheet?: WorksheetTemplate;
  /** Curriculum outcomes for tagging */
  outcomes: CurriculumOutcome[];
  /** Called when worksheet is saved */
  onSave: (worksheet: WorksheetTemplate) => Promise<void>;
  /** Called when user wants to go back */
  onBack: () => void;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Auto-save callback (called with debounce) */
  onAutoSave?: (worksheet: WorksheetTemplate) => void;
}

// ============================================================================
// Draggable Palette Item Overlay
// ============================================================================

interface DragOverlayItemProps {
  definition: ComponentDefinition;
}

function DragOverlayItem({ definition }: DragOverlayItemProps) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-card shadow-lg opacity-90">
      <div className="p-2 rounded-md bg-primary/10 text-primary">
        <FileText className="h-4 w-4" />
      </div>
      <div>
        <p className="text-sm font-medium">{definition.label}</p>
        <p className="text-xs text-muted-foreground">{definition.description}</p>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function WorksheetBuilder({
  initialWorksheet,
  outcomes,
  onSave,
  onBack,
  isSaving = false,
  onAutoSave,
}: WorksheetBuilderProps) {
  // Worksheet state
  const [worksheet, setWorksheet] = useState<WorksheetTemplate>(
    () => initialWorksheet || createEmptyWorksheet()
  );
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(
    null
  );
  const [showGrid, setShowGrid] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [confirmBackOpen, setConfirmBackOpen] = useState(false);

  // Drag state
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [activeDragDefinition, setActiveDragDefinition] =
    useState<ComponentDefinition | null>(null);

  // History for undo/redo (simple implementation)
  const [history, setHistory] = useState<WorksheetTemplate[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isHistoryAction = useRef(false);

  // Auto-save debounce
  const autoSaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement to start drag
      },
    })
  );

  // Get selected component
  const selectedComponent = useMemo(() => {
    return worksheet.components.find((c) => c.id === selectedComponentId) || null;
  }, [worksheet.components, selectedComponentId]);

  // Update grading config when components change
  useEffect(() => {
    const totalPoints = worksheet.components.reduce(
      (sum, c) => sum + (c.points || 0),
      0
    );
    const autoGradablePoints = worksheet.components
      .filter((c) => c.autoGradable)
      .reduce((sum, c) => sum + (c.points || 0), 0);

    setWorksheet((prev) => ({
      ...prev,
      gradingConfig: {
        ...prev.gradingConfig!,
        totalPoints,
        autoGradablePoints,
        teacherReviewPoints: totalPoints - autoGradablePoints,
      },
    }));
  }, [worksheet.components]);

  // Add to history
  const addToHistory = useCallback((ws: WorksheetTemplate) => {
    if (isHistoryAction.current) {
      isHistoryAction.current = false;
      return;
    }

    setHistory((prev) => {
      // Trim history if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      return [...newHistory, JSON.parse(JSON.stringify(ws))].slice(-50); // Keep last 50
    });
    setHistoryIndex((prev) => Math.min(prev + 1, 49));
  }, [historyIndex]);

  // Handle worksheet changes with history
  const updateWorksheet = useCallback(
    (updater: (prev: WorksheetTemplate) => WorksheetTemplate) => {
      setWorksheet((prev) => {
        const updated = updater(prev);
        addToHistory(updated);
        setHasUnsavedChanges(true);

        // Trigger auto-save
        if (onAutoSave) {
          if (autoSaveTimeout.current) {
            clearTimeout(autoSaveTimeout.current);
          }
          autoSaveTimeout.current = setTimeout(() => {
            onAutoSave(updated);
          }, 2000);
        }

        return updated;
      });
    },
    [addToHistory, onAutoSave]
  );

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isHistoryAction.current = true;
      setHistoryIndex((prev) => prev - 1);
      setWorksheet(JSON.parse(JSON.stringify(history[historyIndex - 1])));
      setHasUnsavedChanges(true);
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isHistoryAction.current = true;
      setHistoryIndex((prev) => prev + 1);
      setWorksheet(JSON.parse(JSON.stringify(history[historyIndex + 1])));
      setHasUnsavedChanges(true);
    }
  }, [history, historyIndex]);

  // Handle title change
  const handleTitleChange = useCallback(
    (title: string) => {
      updateWorksheet((prev) => ({ ...prev, title }));
    },
    [updateWorksheet]
  );

  // Drag handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    setActiveDragId(active.id as string);

    // Check if dragging from palette
    if (active.data.current?.type === "palette-item") {
      setActiveDragDefinition(active.data.current.definition);
    }
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      setActiveDragId(null);
      setActiveDragDefinition(null);

      // Handle dropping from palette onto canvas
      if (
        active.data.current?.type === "palette-item" &&
        over?.id === "worksheet-canvas"
      ) {
        const definition = active.data.current.definition as ComponentDefinition;

        // Calculate drop position (use center of canvas for now)
        const newComponent = createComponent(definition, { x: 0.1, y: 0.1 + worksheet.components.length * 0.12 });

        updateWorksheet((prev) => ({
          ...prev,
          components: [...prev.components, newComponent],
        }));

        // Select the new component
        setSelectedComponentId(newComponent.id);
        toast.success(`Added ${definition.label}`);
      }

      // Handle moving component within canvas
      if (
        active.data.current?.type === "canvas-component" &&
        over?.id === "worksheet-canvas"
      ) {
        // Position update would be handled by delta in drag end
        // For now, we just update based on the transform
      }
    },
    [worksheet.components.length, updateWorksheet]
  );

  // Component operations
  const handleSelectComponent = useCallback((id: string | null) => {
    setSelectedComponentId(id);
  }, []);

  const handleDeleteComponent = useCallback(
    (id: string) => {
      updateWorksheet((prev) => ({
        ...prev,
        components: prev.components.filter((c) => c.id !== id),
      }));

      if (selectedComponentId === id) {
        setSelectedComponentId(null);
      }

      toast.success("Component deleted");
    },
    [selectedComponentId, updateWorksheet]
  );

  const handleDuplicateComponent = useCallback(
    (id: string) => {
      const component = worksheet.components.find((c) => c.id === id);
      if (!component) return;

      const newComponent: WorksheetComponent = {
        ...JSON.parse(JSON.stringify(component)),
        id: generateComponentId(),
        position: {
          ...component.position,
          y: component.position.y + 0.1, // Offset below
        },
      };

      updateWorksheet((prev) => ({
        ...prev,
        components: [...prev.components, newComponent],
      }));

      setSelectedComponentId(newComponent.id);
      toast.success("Component duplicated");
    },
    [worksheet.components, updateWorksheet]
  );

  const handleUpdateComponentPosition = useCallback(
    (id: string, position: { x: number; y: number }) => {
      updateWorksheet((prev) => ({
        ...prev,
        components: prev.components.map((c) =>
          c.id === id
            ? {
                ...c,
                position: {
                  ...c.position,
                  x: Math.max(0, Math.min(1 - c.position.width, position.x)),
                  y: Math.max(0, Math.min(1 - c.position.height, position.y)),
                },
              }
            : c
        ),
      }));
    },
    [updateWorksheet]
  );

  const handleUpdateComponent = useCallback(
    (id: string, updates: Partial<WorksheetComponent>) => {
      updateWorksheet((prev) => ({
        ...prev,
        components: prev.components.map((c) =>
          c.id === id ? { ...c, ...updates } : c
        ),
      }));
    },
    [updateWorksheet]
  );

  // Save handler
  const handleSave = useCallback(async () => {
    try {
      await onSave(worksheet);
      setHasUnsavedChanges(false);
      toast.success("Worksheet saved");
    } catch (error) {
      console.error("Failed to save worksheet:", error);
      toast.error("Failed to save worksheet");
    }
  }, [worksheet, onSave]);

  // Back handler with unsaved changes check
  const handleBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setConfirmBackOpen(true);
    } else {
      onBack();
    }
  }, [hasUnsavedChanges, onBack]);

  const handleConfirmBack = useCallback(() => {
    setConfirmBackOpen(false);
    onBack();
  }, [onBack]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
      // Ctrl/Cmd + Z to undo
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y to redo
      if (
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "z") ||
        ((e.ctrlKey || e.metaKey) && e.key === "y")
      ) {
        e.preventDefault();
        handleRedo();
      }
      // Delete to remove selected component
      if (e.key === "Delete" && selectedComponentId) {
        handleDeleteComponent(selectedComponentId);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSave, handleUndo, handleRedo, selectedComponentId, handleDeleteComponent]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="h-screen flex flex-col bg-background">
        {/* Top Bar */}
        <header className="h-14 border-b bg-card flex items-center px-4 gap-4 shrink-0">
          {/* Back button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={handleBack}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Back to worksheets</TooltipContent>
          </Tooltip>

          {/* Title */}
          <div className="flex-1 max-w-md">
            <Input
              value={worksheet.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="text-lg font-semibold border-transparent hover:border-input focus:border-input"
              placeholder="Worksheet title..."
            />
          </div>

          {/* Status badge */}
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                Unsaved
              </Badge>
            )}
            {worksheet.status === "draft" && (
              <Badge variant="secondary">Draft</Badge>
            )}
            {worksheet.status === "published" && (
              <Badge variant="default">Published</Badge>
            )}
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-2 border-l pl-4 ml-4">
            {/* Undo/Redo */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Undo (Ctrl+Z)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                >
                  <Redo2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Redo (Ctrl+Shift+Z)</TooltipContent>
            </Tooltip>

            {/* Grid toggle */}
            <div className="flex items-center gap-2 border-l pl-4 ml-2">
              <Switch
                id="showGrid"
                checked={showGrid}
                onCheckedChange={setShowGrid}
              />
              <Label htmlFor="showGrid" className="text-sm cursor-pointer">
                <Grid3X3 className="h-4 w-4" />
              </Label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 border-l pl-4 ml-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => setPreviewOpen(true)}
                  className="gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </Button>
              </TooltipTrigger>
              <TooltipContent>Preview worksheet</TooltipContent>
            </Tooltip>

            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="gap-2"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Component Palette */}
          <ComponentPalette className="w-64 shrink-0" />

          {/* Center - Canvas */}
          <WorksheetCanvas
            components={worksheet.components}
            layout={worksheet.layout}
            selectedComponentId={selectedComponentId}
            showGrid={showGrid}
            onSelectComponent={handleSelectComponent}
            onDeleteComponent={handleDeleteComponent}
            onDuplicateComponent={handleDuplicateComponent}
            onUpdateComponentPosition={handleUpdateComponentPosition}
          />

          {/* Right Sidebar - Property Editor */}
          <PropertyEditor
            component={selectedComponent}
            outcomes={outcomes}
            onUpdate={handleUpdateComponent}
          />
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeDragId && activeDragDefinition && (
            <DragOverlayItem definition={activeDragDefinition} />
          )}
        </DragOverlay>

        {/* Preview Dialog */}
        <WorksheetPreview
          worksheet={worksheet}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
        />

        {/* Confirm Back Dialog */}
        <AlertDialog open={confirmBackOpen} onOpenChange={setConfirmBackOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Are you sure you want to leave? Your
                changes will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmBack}>
                Leave without saving
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DndContext>
  );
}
